import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import endpoints from "../../../api/endpoints";
import api from "../../../api/axios";
import { extractApiErrorMessage } from "../../../utils/apiError";
import { downloadFile } from "../../../utils/downloadUtils";

// ==================== TYPES ====================
export interface TuitionApplication {
  id: number;
  applicationType: string;
  applicationTypeName: string;
  applicationDate: string;
  applicationYear: string;
  applicationTerm: string;
  applicationProgramId: number;
  status: string;
  applicationComment: string;
}

export interface PaginationMeta {
  pageNumber: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TuitionApplicationsResponse {
  items: TuitionApplication[];
  meta: PaginationMeta;
}

// ==================== STATE TYPES ====================
interface TuitionViewMyStatusState {
  applications: TuitionApplication[];
  items: any[];
  documents: any[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  lastOperationType: string | null;
  currentPage: number;
  perPage: number;
}

const initialState: TuitionViewMyStatusState = {
  applications: [],
  meta: null,
  loading: false,
  error: null,
  lastOperationType: null,
  currentPage: 1,
  perPage: 10,
  items: [],
  documents: [],
};

// ==================== UTILITY FUNCTIONS ====================
/**
 * Normalizes API response data into array format
 */
const normalizeResponseData = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload?.items && Array.isArray(payload.items)) {
    return payload.items;
  }
  if (payload?.data && Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload ? [payload] : [];
};

/**
 * Generic pending handler for all async operations
 */
const handlePending = (state: TuitionViewMyStatusState) => {
  state.loading = true;
  state.error = null;
};

/**
 * Generic rejected handler for all async operations with proper type handling
 */
const handleRejected = (
  state: TuitionViewMyStatusState,
  action: PayloadAction<unknown>,
) => {
  state.loading = false;
  // Handle both string payload and SerializedError
  if (typeof action.payload === "string") {
    state.error = action.payload;
  } else if (
    action.payload &&
    typeof action.payload === "object" &&
    "message" in action.payload
  ) {
    state.error = (action.payload as { message: string }).message;
  } else {
    state.error = "An error occurred";
  }
};

/**
 * Generic fulfilled handler for document operations
 */
const handleDocumentsFulfilled = (
  state: TuitionViewMyStatusState,
  action: PayloadAction<any>,
) => {
  state.loading = false;
  state.documents = normalizeResponseData(action.payload);
};

/**
 * Generic fulfilled handler for items/details operations
 */
const handleItemsFulfilled = (
  state: TuitionViewMyStatusState,
  action: PayloadAction<any>,
) => {
  state.loading = false;
  state.error = null;
  state.items = normalizeResponseData(action.payload);
};

/**
 * Generic rejected handler that also clears the target data field
 */
const createRejectedHandler = (
  dataField: keyof Pick<
    TuitionViewMyStatusState,
    "applications" | "documents" | "items"
  >,
) => {
  return (state: TuitionViewMyStatusState, action: PayloadAction<unknown>) => {
    handleRejected(state, action);
    state[dataField] = [];
  };
};

// ==================== ASYNC THUNKS ====================
export const fetchTuitionApplications = createAsyncThunk<
  TuitionApplicationsResponse,
  { applType: string; page?: number; perPage?: number },
  { rejectValue: string }
>(
  "tuitionViewMyStatus/fetchTuitionApplications",
  async ({ applType, page = 1, perPage = 10 }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.tuitionViewDocuments.getApplications}?applType=${applType}&page=${page}&per_page=${perPage}`;
      const response = await api.get<TuitionApplicationsResponse>(url);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data
          ? extractApiErrorMessage(error, "Failed to fetch applications")
          : "Failed to fetch applications",
      );
    }
  },
);

export const getDocumentsList = createAsyncThunk<
  any,
  Record<string, any> | undefined,
  { rejectValue: string }
>(
  "viewDocument/getDocumentsList",
  async (
    params: Record<string, any> | undefined,
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = { signal };
      if (params) options.params = params;
      const applicationId = params?.applicationId;
      const url =
        endpoints.tuition.tuitionViewDocuments.documentsList(applicationId);
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        error?.message || "Failed to fetch documents list",
      );
    }
  },
);

export const uploadDocument = createAsyncThunk<
  { applicationId: string; description: string; fileName: string },
  {
    applicationId: string;
    file: File;
    description: string;
  },
  { rejectValue: string }
>(
  "viewDocument/uploadDocument",
  async ({ applicationId, file, description }, { rejectWithValue }) => {
    try {
      const documents = new FormData();
      documents.append("file", file);
      documents.append("description", description);

      await api.post(
        endpoints.tuition.tuitionViewDocuments.uploadDocument(applicationId),
        documents,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        applicationId,
        description,
        fileName: file.name,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload document");
    }
  },
);

export const downloadDocument = createAsyncThunk<
  { applicationId: string; documentId: string },
  {
    applicationId: string;
    documentId: string;
    fileName?: string;
  },
  { rejectValue: string }
>(
  "viewDocument/downloadDocument",
  async ({ applicationId, documentId, fileName }, { rejectWithValue }) => {
    try {
      const url = endpoints.tuition.tuitionViewDocuments.downloadDocument(
        applicationId,
        documentId,
      );
      const response = await api.get(url, { responseType: "blob" });
      downloadFile({
        response,
        documentId,
        fileName,
        defaultFileName: "document"
      });

      return { applicationId, documentId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to download document");
    }
  },
);

export const fetchApplicationDetails = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "viewDocument/fetchApplicationDetails",
  async (applicationId: string, { rejectWithValue, signal }) => {
    try {
      const url =
        endpoints.tuition.tuitionViewDocuments.applicationDetails(
          applicationId,
        );
      const response = await api.get(url, { signal });
      const mappedData = {
        ...response.data,
        empShift: response.data.shiftStdHours ?? "",
      };
      return mappedData;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch application details"),
      );
    }
  },
);
export const downloadPrintApplication = createAsyncThunk<
  any,
  { applicationId: string },
  { rejectValue: string }
>(
  "viewDocument/downloadPrintApplication",
  async ({ applicationId }, { rejectWithValue, signal }) => {
    try {
      const url =
        endpoints.tuition.tuitionViewDocuments.printApplication(applicationId);

      const response = await api.get(url, { signal });

      const htmlContent = response.data?.htmlContent;
      if (!htmlContent) {
        return rejectWithValue("No HTML content received from backend");
      }

      const printWindow = window.open("about:blank", "_blank");
      if (printWindow) {
        printWindow.document.writeln(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 50);
      } else {
        alert(
          "Please allow pop-ups for this site to view the content in a new tab.",
        );
      }

      return { applicationId };
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        error?.message || "Failed to fetch print application details",
      );
    }
  },
);

export const downloadPrintFaxCover = createAsyncThunk<
  any,
  { applicationId: string },
  { rejectValue: string }
>(
  "viewDocument/downloadPrintFaxCover",
  async ({ applicationId }, { rejectWithValue, signal }) => {
    try {
      const url =
        endpoints.tuition.tuitionViewDocuments.printFaxCover(applicationId);

      const response = await api.get(url, { signal });

      const htmlContent = response.data?.htmlContent;
      if (!htmlContent) {
        return rejectWithValue("No HTML content received from backend");
      }

      const printWindow = window.open("about:blank", "_blank");
      if (printWindow) {
        printWindow.document.writeln(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 50);
      } else {
        alert(
          "Please allow pop-ups for this site to view the content in a new tab.",
        );
      }
      return { applicationId };
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        error?.message || "Failed to fetch or generate fax cover PDF",
      );
    }
  },
);

// ==================== SLICE ====================
const tuitionViewMyStatusSlice = createSlice({
  name: "tuitionViewMyStatus",
  initialState,
  reducers: {
    resetApplications: (state) => {
      state.applications = [];
      state.meta = null;
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ========== Fetch Tuition Applications ==========
    builder
      .addCase(fetchTuitionApplications.pending, handlePending)
      .addCase(
        fetchTuitionApplications.fulfilled,
        (state, action: PayloadAction<TuitionApplicationsResponse>) => {
          state.loading = false;
          state.applications = action.payload.items;
          state.meta = action.payload.meta;
          state.currentPage = action.payload.meta.pageNumber;
        },
      )
      .addCase(
        fetchTuitionApplications.rejected,
        createRejectedHandler("applications"),
      );

    // ========== Get Documents List ==========
    builder
      .addCase(getDocumentsList.pending, handlePending)
      .addCase(getDocumentsList.fulfilled, handleDocumentsFulfilled)
      .addCase(getDocumentsList.rejected, createRejectedHandler("documents"));

    // ========== Upload Document ==========
    builder
      .addCase(uploadDocument.pending, handlePending)
      .addCase(uploadDocument.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadDocument.rejected, handleRejected);

    // ========== Download Document ==========
    builder
      .addCase(downloadDocument.pending, handlePending)
      .addCase(downloadDocument.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadDocument.rejected, handleRejected);

    // ========== Fetch Application Details ==========
    builder
      .addCase(fetchApplicationDetails.pending, handlePending)
      .addCase(fetchApplicationDetails.fulfilled, handleItemsFulfilled)
      .addCase(
        fetchApplicationDetails.rejected,
        createRejectedHandler("items"),
      );

    // ========== Get Print Application ==========
    builder
      .addCase(downloadPrintApplication.pending, handlePending)
      .addCase(downloadPrintApplication.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(downloadPrintApplication.rejected, handleRejected);

    // ========== Get Print Fax Cover ==========
    builder
      .addCase(downloadPrintFaxCover.pending, handlePending)
      .addCase(downloadPrintFaxCover.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(downloadPrintFaxCover.rejected, handleRejected);
  },
});

export const { resetApplications, setCurrentPage, setPerPage } =
  tuitionViewMyStatusSlice.actions;

export default tuitionViewMyStatusSlice.reducer;
